/**
 * /admin/login — email + password, redirects to ?redirect= or /admin/quotes.
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Mail } from "lucide-react";

import { adminLogin, persistAdminAuth } from "../../lib/admin-api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await adminLogin(email, password);
      if (data?.requires_2fa) {
        setError("2FA required but not yet wired in Phase 4.");
        return;
      }
      persistAdminAuth(data);
      const redirect = params.get("redirect");
      navigate(redirect ? decodeURIComponent(redirect) : "/admin/quotes", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message ||
            (err?.response?.status === 429
              ? "Too many attempts. Try again later."
              : "Couldn't sign in. Check email/password.");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-[#F3F4F6]" data-testid="admin-login-page">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-12 sm:px-8">
        <div className="rounded-3xl bg-white p-8 text-[#0F172A] shadow-xl sm:p-10">
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8A24B]">
            ShutterKART · Admin
          </p>
          <h1 className="mt-3 font-display text-3xl">Sign in</h1>
          <p className="mt-2 text-sm text-[#0F172A]/55">
            Operator console. Use your ShutterKART team credentials.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.26em] text-[#0F172A]/55">
                Email
              </span>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-[#0F172A]/15 bg-white px-3 focus-within:border-[#C8A24B]">
                <Mail className="h-4 w-4 text-[#0F172A]/40" />
                <input
                  type="email"
                  data-testid="admin-login-email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-[#0F172A]/35"
                  placeholder="you@shutterkart.com"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.26em] text-[#0F172A]/55">
                Password
              </span>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-[#0F172A]/15 bg-white px-3 focus-within:border-[#C8A24B]">
                <Lock className="h-4 w-4 text-[#0F172A]/40" />
                <input
                  type="password"
                  data-testid="admin-login-password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-[#0F172A]/35"
                  placeholder="••••••••"
                />
              </div>
            </label>

            {error ? (
              <p
                data-testid="admin-login-error"
                role="alert"
                className="rounded-lg bg-[#B0413E]/10 px-3 py-2 text-xs text-[#B0413E]"
              >
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={!email || !password || submitting}
              className={[
                "inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.22em] transition",
                email && password && !submitting
                  ? "bg-[#0F172A] text-white hover:bg-[#1f2937]"
                  : "cursor-not-allowed bg-[#0F172A]/25 text-white",
              ].join(" ")}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Sign in
            </button>
          </form>

          <p className="mt-6 text-[10px] uppercase tracking-[0.28em] text-[#0F172A]/45">
            Buyer? <a href="/" className="text-[#9A7A2F]">Return to site</a>
          </p>
        </div>
      </div>
    </div>
  );
}
